import Phaser from 'phaser';

export class MarqueeManager {
  constructor(scene) {
    this.scene = scene;
    this.marqueeContainer = null;
    this.marqueeTexts = [];
    this.isInitialized = false;
    this.maxMessages = 1; // Only show one message at a time for proper spacing
    this.scrollSpeed = 30; // pixels per second (slow and readable)
    this.messageSpacing = 100; // pixels between messages (minimum gap)
    this.messageDuration = 12000; // milliseconds minimum duration

    // Priority queues for messages
    this.queues = {
      high: [],    // Game messages (must-show)
      medium: [],  // Join messages
      low: []      // Other messages
    };

    this.currentMessage = null; // Currently displaying message
    this.queueCheckTimer = null; // Timer for periodically checking queue
  }

  // Add message to priority queue
  queueMessage(text, options = {}, priority = 'low') {
    const messageData = { text, options, timestamp: Date.now() };

    // Add to appropriate queue
    if (this.queues[priority]) {
      this.queues[priority].push(messageData);
      //console.log(`[MarqueeManager] Queued ${priority} priority message:`, text);
    } else {
      //console.warn(`[MarqueeManager] Invalid priority: ${priority}, using low`);
      this.queues.low.push(messageData);
    }

    // Immediately try to show the message if possible (queue checking will also handle this)
    this.tryShowNextMessage();
  }

  // Get next message from highest priority non-empty queue
  dequeueNextMessage() {
    if (this.queues.high.length > 0) {
      return this.queues.high.shift();
    }
    if (this.queues.medium.length > 0) {
      return this.queues.medium.shift();
    }
    if (this.queues.low.length > 0) {
      return this.queues.low.shift();
    }
    return null;
  }

  // Check if we can show the next message (based on spacing)
  canShowNextMessage() {
    if (!this.currentMessage) return true;

    // Check if current message has scrolled far enough to allow next message
    const currentText = this.currentMessage.text;
    if (!currentText) return true;

    const sceneForCalc = (this.currentMessage.scene || this.scene);
    const camera = sceneForCalc && sceneForCalc.cameras && sceneForCalc.cameras.main;
    if (!camera) return true; // If no camera yet (scene switched), allow next

    const screenWidth = camera.width;
    const currentX = currentText.x;
    const messageWidth = currentText.width || 0;

    // Allow next message when current message is 20px from right edge
    return (currentX + messageWidth) < (screenWidth - 20);
  }

  // Try to show next message from queue
  tryShowNextMessage() {
    // Ensure we have a valid scene reference before trying to show messages
    this.ensureValidScene();
    
    if (!this.currentMessage || this.canShowNextMessage()) {
      const nextMessage = this.dequeueNextMessage();
      if (nextMessage) {
        this.showMessageFromQueue(nextMessage);
      }
    }
  }

  // Ensure we have a valid scene reference
  ensureValidScene() {
    if (!this.scene || this.scene.sys?.isDestroyed || !this.scene.sys?.isActive) {
      // Try to find an active scene from the game
      const game = this.scene?.game || window.game;
      if (game && game.scene && game.scene.scenes) {
        const activeScene = Object.values(game.scene.scenes).find(scene => 
          scene && scene.sys && scene.sys.isActive && !scene.sys.isDestroyed
        );
        if (activeScene) {
          this.scene = activeScene;
          //console.log('[MarqueeManager] Updated scene reference to active scene');
        }
      }
    }
  }

  // Show a message from the queue
  showMessageFromQueue(messageData) {
    const { text, options } = messageData;
    this.addMessage(text, options);
  }

  // Clear all queues
  clearQueues() {
    this.queues.high = [];
    this.queues.medium = [];
    this.queues.low = [];
    //console.log('[MarqueeManager] Cleared all message queues');
  }

  // Start periodic queue checking
  startQueueChecking() {
    if (this.queueCheckTimer) {
      this.queueCheckTimer.destroy();
    }

    // Ensure we have a valid scene reference
    this.ensureValidScene();

    // Only start timer if we have a valid scene
    if (this.scene && this.scene.sys && !this.scene.sys.isDestroyed && this.scene.sys.isActive) {
      // Check queue every 500ms
      this.queueCheckTimer = this.scene.time.addEvent({
        delay: 500, // Check every half second
        callback: () => this.tryShowNextMessage(),
        loop: true
      });

      //console.log('[MarqueeManager] Started queue checking timer');
    } else {
      console.warn('[MarqueeManager] No valid scene for queue checking timer');
    }
  }

  // Stop queue checking
  stopQueueChecking() {
    if (this.queueCheckTimer) {
      this.queueCheckTimer.destroy();
      this.queueCheckTimer = null;
      //console.log('[MarqueeManager] Stopped queue checking timer');
    }
  }

  init() {
    if (this.isInitialized) return;

    // Create marquee container on UI camera
    this.marqueeContainer = this.scene.add.container(0, 0);
    this.marqueeContainer.setDepth(10000); // High depth to appear above everything

    // Make sure it's on the UI camera (ignore world camera)
    if (this.scene.uiCamera) {
      this.scene.worldCamera.ignore(this.marqueeContainer);
    }

    // Store reference to this marquee manager globally
    const game = this.scene?.game;
    if (game) {
      game.marqueeManager = this;
    }

    // Also store globally for static access
    window.globalMarqueeManager = this;

    // Start periodic queue checking
    this.startQueueChecking();

    this.isInitialized = true;
    //console.log('[MarqueeManager] Initialized');
  }

  addMessage(text, options = {}) {
    if (!this.isInitialized) {
      //console.warn('[MarqueeManager] Not initialized, cannot add message');
      return;
    }

    // Ensure we have a valid scene reference
    this.ensureValidScene();

    if (!this.scene || !this.scene.sys || this.scene.sys.isDestroyed || !this.scene.sys.isActive) {
      //console.warn('[MarqueeManager] No valid scene available, cannot add message');
      return;
    }

    const {
      color = '#FFFFFF',
      fontSize = '16px',
      fontFamily = 'Arial',
      stroke = '#000000',
      strokeThickness = 3
    } = options;

    // Get the currently active scene to create text objects
    let activeScene = this.scene;
    const game = this.scene?.game; // Get game from current scene
    //console.log('[MarqueeManager] Finding active scene - current scene:', this.scene?.key, 'game exists:', !!game);

    if (game && game.scene) {
      const activeScenes = game.scene.getScenes(true);
      //console.log('[MarqueeManager] Active scenes found:', activeScenes.map(s => s.key));
      if (activeScenes.length > 0) {
        activeScene = activeScenes[0]; // Use the first active scene
        //console.log('[MarqueeManager] Using active scene:', activeScene.key);
      }
    } else {
      //console.log('[MarqueeManager] Game or game.scene not available');
    }

    // Ensure we have a valid scene to work with
    if (!activeScene) {
      //console.warn('[MarqueeManager] No active scene found, using original scene');
      activeScene = this.scene;
    }

    // Check if the scene is valid and has the add method
    if (!activeScene || !activeScene.add || !activeScene.add.text) {
      /*console.warn('[MarqueeManager] Scene is null or destroyed, cannot create text:', {
        activeScene: !!activeScene,
        hasAdd: !!(activeScene && activeScene.add),
        hasAddText: !!(activeScene && activeScene.add && activeScene.add.text),
        sceneKey: activeScene?.key
      });*/
      return;
    }

    // Create text object on the active scene
    const marqueeText = activeScene.add.text(0, 0, text, {
      fontFamily: fontFamily,
      fontSize: fontSize,
      color: color,
      stroke: stroke,
      strokeThickness: strokeThickness,
      align: 'left'
    });

    // Position at right edge of screen
    const camera = activeScene?.cameras?.main;
    const screenWidth = camera ? camera.width : (window.innerWidth || 800);
    const screenHeight = camera ? camera.height : (window.innerHeight || 600);

    // All messages at the same Y position (top of screen with small margin)
    const yPosition = 2;

    marqueeText.setPosition(screenWidth + 50, yPosition);
    marqueeText.setDepth(10001);

    // Ensure the text is on the UI camera (ignore world camera)
    if (activeScene.worldCamera) {
      activeScene.worldCamera.ignore(marqueeText);
    }

    // Add to container for organization (but don't rely on it for cross-scene rendering)
    if (this.marqueeContainer && this.marqueeContainer.scene === activeScene) {
      this.marqueeContainer.add(marqueeText);
    }

    // Set as current message
    this.currentMessage = {
      text: marqueeText,
      startTime: Date.now(),
      duration: this.messageDuration,
      yPosition: yPosition,
      scene: activeScene
    };

    this.marqueeTexts.push(this.currentMessage);

    // Start scroll animation
    this.animateMessage(marqueeText, screenWidth);

    //console.log('[MarqueeManager] Showing message:', text, 'on scene:', activeScene?.key || 'unknown');
  }

  animateMessage(textObject, screenWidth) {
    // Calculate distance to scroll (from right edge to left edge + some buffer)
    const scrollDistance = screenWidth + textObject.width + 100;

    // Calculate duration based on scroll distance and speed for smooth scrolling
    // scrollSpeed is pixels per second, so duration = distance / speed * 1000ms
    const calculatedDuration = (scrollDistance / this.scrollSpeed) * 1000;

    // Use the standard message duration for consistency
    const finalDuration = this.messageDuration;

    //console.log(`[MarqueeManager] Scrolling message: distance=${scrollDistance}px, speed=${this.scrollSpeed}px/s, duration=${finalDuration}ms`);

    // Create scroll animation on the text object's scene (ensures correct lifecycle)
    const tweenScene = (textObject && textObject.scene) ? textObject.scene : this.scene;
    if (!tweenScene || !tweenScene.tweens) return;
    tweenScene.tweens.add({
      targets: textObject,
      x: -textObject.width - 100,
      duration: finalDuration,
      ease: 'Linear',
      onComplete: () => {
        this.removeMessage(textObject);
        // Clear current message (queue checking will handle showing next message)
        this.currentMessage = null;
      }
    });
  }

  removeMessage(textObject) {
    // Remove from container if it exists
    if (this.marqueeContainer) {
      this.marqueeContainer.remove(textObject);
    }

    // Remove from tracking array
    const index = this.marqueeTexts.findIndex(item => item.text === textObject);
    if (index !== -1) {
      this.marqueeTexts.splice(index, 1);
    }

    // Destroy the text object (it will be destroyed from its scene)
    if (textObject && typeof textObject.destroy === 'function') {
      textObject.destroy();
    }
  }

  cleanupOldMessages() {
    const now = Date.now();
    const messagesToRemove = [];

    for (let i = 0; i < this.marqueeTexts.length; i++) {
      const message = this.marqueeTexts[i];
      if (now - message.startTime > message.duration) {
        messagesToRemove.push(message.text);
      }
    }

    messagesToRemove.forEach(textObject => {
      this.removeMessage(textObject);
    });
  }

  clear() {
    // Remove all messages
    this.marqueeTexts.forEach(item => {
      this.marqueeContainer.remove(item.text);
      if (item.text && typeof item.text.destroy === 'function') {
        item.text.destroy();
      }
    });
    this.marqueeTexts = [];
    //console.log('[MarqueeManager] Cleared all messages');
  }

  destroy() {
    // Stop queue checking
    this.stopQueueChecking();

    this.clear();
    if (this.marqueeContainer) {
      this.marqueeContainer.destroy();
      this.marqueeContainer = null;
    }
    this.isInitialized = false;
  }

  // Static method to add message from anywhere
  static addMessage(text, options = {}, priority = 'low') {
    // Filter out duration from options since it's fixed
    const { duration, ...filteredOptions } = options;

    // First try the global marquee manager reference
    if (window.globalMarqueeManager) {
      window.globalMarqueeManager.queueMessage(text, filteredOptions, priority);
      return;
    }

    // Try to find game through window ref
    let game = window.phaserGameRef?.current;

    // If not available, try to find through any Phaser scene
    if (!game) {
      // Find any Phaser game instance through the global registry
      if (window.Phaser && window.Phaser.GAMES) {
        game = window.Phaser.GAMES[window.Phaser.GAMES.length - 1];
      }
    }

    if (game && game.scene) {
      const activeScenes = game.scene.getScenes(true);

      // First check if any scene has a marquee manager
      for (const scene of activeScenes) {
        if (scene.marqueeManager) {
          scene.marqueeManager.queueMessage(text, filteredOptions, priority);
          return;
        }
      }

      // Fall back to global game marquee manager
      if (game.marqueeManager) {
        game.marqueeManager.queueMessage(text, filteredOptions, priority);
        return;
      }
    }

    console.warn('[MarqueeManager] Global marquee manager not available', {
      globalManager: !!window.globalMarqueeManager,
      game: !!game,
      marqueeManager: !!(game?.marqueeManager),
      phaserGameRef: !!window.phaserGameRef,
      current: !!window.phaserGameRef?.current,
      phaserGames: window.Phaser?.GAMES?.length || 0
    });
  }

  // Static method to clear all messages
  static clear() {
    // First try the global marquee manager reference
    if (window.globalMarqueeManager) {
      window.globalMarqueeManager.clear();
      return;
    }

    // Try to find game through window ref
    let game = window.phaserGameRef?.current;

    // If not available, try to find through any Phaser scene
    if (!game) {
      // Find any Phaser game instance through the global registry
      if (window.Phaser && window.Phaser.GAMES) {
        game = window.Phaser.GAMES[window.Phaser.GAMES.length - 1];
      }
    }

    if (game && game.scene) {
      const activeScenes = game.scene.getScenes(true);
      for (const scene of activeScenes) {
        if (scene.marqueeManager) {
          scene.marqueeManager.clear();
          return;
        }
      }
    }

    // Fall back to global game marquee manager
    if (game && game.marqueeManager) {
      game.marqueeManager.clear();
      return;
    }
  }

  // Static method to clear all queues
  static clearQueues() {
    // First try the global marquee manager reference
    if (window.globalMarqueeManager) {
      window.globalMarqueeManager.clearQueues();
      return;
    }

    // Try to find game through window ref
    let game = window.phaserGameRef?.current;

    // If not available, try to find through any Phaser scene
    if (!game) {
      // Find any Phaser game instance through the global registry
      if (window.Phaser && window.Phaser.GAMES) {
        game = window.Phaser.GAMES[window.Phaser.GAMES.length - 1];
      }
    }

    if (game && game.scene) {
      const activeScenes = game.scene.getScenes(true);
      for (const scene of activeScenes) {
        if (scene.marqueeManager) {
          scene.marqueeManager.clearQueues();
          return;
        }
      }
    }

    // Fall back to global game marquee manager
    if (game && game.marqueeManager) {
      game.marqueeManager.clearQueues();
      return;
    }
  }
}
